/**
 * apps/automations-portal/src/features/admin/LedgerApprovalsPage.tsx
 *
 * The queue of events waiting on a human decision, oldest first (the API's
 * order — not re-sorted here). Hold is a direct, unconfirmed action; Approve
 * is gated behind a confirmation dialog because it sends something to a real
 * customer. A 409 means someone else already decided — that always wins over
 * whatever this tab believes, so it refetches rather than trying to patch
 * around it.
 *
 * Each row shows the actual wording that would go out, not just the one-line
 * summary. Approving something you cannot see is a guess, and a queue of
 * guesses gets rubber-stamped — which is the failure mode this whole approval
 * step exists to prevent.
 */
import { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { ApiError, adminApi } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format';
import { draftFrom } from '@/lib/draft';
import type { LedgerEventWithClient } from '@/lib/types';

export function LedgerApprovalsPage() {
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [approvals, setApprovals] = useState<LedgerEventWithClient[]>([]);
  const [approveTarget, setApproveTarget] = useState<LedgerEventWithClient | null>(null);
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function load() {
    setState('loading');
    adminApi
      .ledgerApprovals()
      .then((data) => {
        setApprovals(data);
        setState('ready');
      })
      .catch(() => setState('error'));
  }

  useEffect(() => {
    load();
  }, []);

  function removeLocally(eventId: string) {
    setApprovals((prev) => prev.filter((event) => event.id !== eventId));
  }

  async function decide(event: LedgerEventWithClient, decision: 'approve' | 'hold') {
    setDecidingId(event.id);
    setNotice(null);
    try {
      await adminApi.decideApproval(event.id, decision);
      removeLocally(event.id);
      toast(decision === 'approve' ? 'Approved' : 'Held', {
        description:
          decision === 'approve'
            ? `${event.summary} has been sent.`
            : `${event.summary} has been held back.`,
      });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ALREADY_DECIDED') {
        // Someone else's answer always wins over whatever this tab believes —
        // refetch the full list rather than trying to patch the stale row.
        setNotice('Someone already answered this.');
        toast('Someone already answered this.', { description: 'Refreshing the list.' });
        load();
      } else {
        toast('Could not save that decision', {
          description: err instanceof ApiError ? err.message : 'Something went wrong. Try again.',
        });
      }
    } finally {
      setDecidingId(null);
      setApproveTarget(null);
    }
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 p-8">
      <div>
        <h1 className="text-2xl font-semibold">Approvals</h1>
        <p className="text-sm text-muted-foreground">Events waiting on a decision before they go any further.</p>
      </div>

      {notice && <p className="text-sm text-muted-foreground">{notice}</p>}

      {state === 'loading' && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {state === 'error' && (
        <p className="text-sm text-destructive">Could not load approvals. Try refreshing the page.</p>
      )}

      {state === 'ready' && approvals.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <CheckCircle2 className="h-8 w-8 text-success" />
            <p className="text-sm font-medium">Nothing waiting.</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              Every event that needed a decision has had one. New approvals will show up here as they come in.
            </p>
          </CardContent>
        </Card>
      )}

      {state === 'ready' && approvals.length > 0 && (
        <div className="flex flex-col gap-3">
          {approvals.map((event) => (
            <Card key={event.id}>
              <CardContent className="flex flex-col gap-3 py-4">
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">{event.client.name}</span>
                  <span className="text-sm">{event.summary}</span>
                  <span className="text-xs text-muted-foreground">
                    Waiting {formatRelativeTime(event.occurredAt, { suffix: '' })}
                  </span>
                </div>

                <ReviewBody payload={event.payload} />

                <div className="flex items-center justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={decidingId === event.id}
                    onClick={() => void decide(event, 'hold')}
                  >
                    Hold
                  </Button>
                  <Button
                    type="button"
                    disabled={decidingId === event.id}
                    onClick={() => setApproveTarget(event)}
                  >
                    Approve
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={approveTarget !== null} onOpenChange={(open) => !open && setApproveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send this?</DialogTitle>
            <DialogDescription>
              This goes to a real customer. {approveTarget?.summary}
            </DialogDescription>
          </DialogHeader>
          {approveTarget && (
            <div className="max-h-64 overflow-y-auto">
              <ReviewBody payload={approveTarget.payload} />
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setApproveTarget(null)}
              disabled={approveTarget !== null && decidingId === approveTarget.id}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => approveTarget && void decide(approveTarget, 'approve')}
              disabled={approveTarget !== null && decidingId === approveTarget.id}
            >
              {approveTarget !== null && decidingId === approveTarget.id ? 'Approving…' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * What is actually being decided on.
 *
 * Renders nothing at all when the automation attached no wording — an empty
 * bordered box reads as "there is nothing here", which is a different and
 * wrong message from "this block predates drafts being carried".
 */
function ReviewBody({ payload }: { payload: unknown }) {
  const { draft, asked, from } = draftFrom(payload);
  if (!draft && !asked && !from) return null;

  return (
    <div className="flex flex-col gap-2 text-sm">
      {from && (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">From:</span> {from}
        </p>
      )}
      {asked && (
        <p className="border-l-2 border-border pl-3 text-xs whitespace-pre-wrap text-muted-foreground">
          {asked}
        </p>
      )}
      {draft && (
        <div className="max-h-56 overflow-y-auto rounded-md border bg-muted/40 p-3 whitespace-pre-wrap">
          {draft}
        </div>
      )}
    </div>
  );
}
